import { Injectable, NgZone } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ReelBanner } from '../models/reel.model';

interface ReelState {
  isOpen: boolean;
  banner: ReelBanner | null;
}

/**
 * Drives the full-screen Feather Reel overlay — the direct analogue of `AttractionDetailsSheetService`,
 * carrying a resolved `ReelBanner` instead of an attraction code.
 *
 * Hosted once in AppComponent above the router-outlet, so it overlays whatever screen opened it.
 * Opening pushes a synthetic same-URL history entry so browser Back closes the reel; a programmatic
 * navigation tears it down so the overlay + `body.reel-open` scroll-lock never persist over the next
 * screen. An empty-clip banner is inert — `open` is a no-op (iOS parity).
 */
@Injectable({ providedIn: 'root' })
export class ReelService {
  private state = new BehaviorSubject<ReelState>({ isOpen: false, banner: null });

  /** Emits on every open/close; AppComponent binds this with the async pipe. */
  readonly changes$: Observable<ReelState> = this.state.asObservable();

  /** True while our synthetic history entry is on the stack (so Back closes the reel). */
  private hasHistoryEntry = false;

  constructor(private router: Router, private ngZone: NgZone) {
    // Browser Back: the browser has already popped our synthetic entry, so just close in place.
    window.addEventListener('popstate', this.onPopState);

    // Any programmatic route change while the reel is open must close it; the router is taking over
    // history, so we must not pop our own entry here.
    this.router.events
      .pipe(filter(e => e instanceof NavigationStart))
      .subscribe(() => {
        if (this.isOpen) {
          this.hasHistoryEntry = false;
          this.reset();
        }
      });
  }

  get isOpen(): boolean {
    return this.state.value.isOpen;
  }

  get banner(): ReelBanner | null {
    return this.state.value.banner;
  }

  /** Open the reel over the current screen. Inert for a banner with no clips (iOS parity). */
  open(banner: ReelBanner): void {
    if (!banner || banner.clips.length === 0) return;
    document.body.classList.add('reel-open');
    // Push a same-URL history entry once per open session so Back closes the reel.
    if (!this.hasHistoryEntry) {
      history.pushState({ reel: true }, '', location.href);
      this.hasHistoryEntry = true;
    }
    this.state.next({ isOpen: true, banner });
  }

  /** User-initiated close (Close button / Back / Escape). Pops the synthetic history entry. */
  close(): void {
    const hadEntry = this.hasHistoryEntry;
    this.reset();
    if (hadEntry) {
      history.back();
    }
  }

  private onPopState = (): void => {
    if (!this.isOpen) return;
    this.ngZone.run(() => {
      this.hasHistoryEntry = false;
      this.reset();
    });
  };

  /** Clear reel state (and body scroll-lock) without touching history. */
  private reset(): void {
    this.hasHistoryEntry = false;
    document.body.classList.remove('reel-open');
    this.state.next({ isOpen: false, banner: null });
  }
}
